'use client';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, CreditCard, Smartphone, Users, Shield, ArrowRight, Loader2, Edit, Mail, Phone, Check, Copy } from 'lucide-react';
import { useAuth } from '@/providers/auth-provider';
import { useWallet } from '@/hooks/useWallet';
import { useNumbers } from '@/hooks/useNumbers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatCurrency, formatDateTime, cn, getInitials } from '@/lib/utils';
import { toast } from '@/components/ui/sonner';
import { TrendingUp } from 'lucide-react';

export function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const { balance, transactions, fetchBalance, fetchTransactions } = useWallet();
  const { assignments, fetchAssignments } = useNumbers();
  const [activeTab, setActiveTab] = useState<'overview' | 'wallet' | 'numbers' | 'referrals' | 'activity'>('overview');
  const [editing, setEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    timezone: 'UTC',
    language: 'en',
  });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName,
        lastName: user.lastName,
        phone: '',
        timezone: 'UTC',
        language: 'en',
      });
      fetchBalance();
      fetchTransactions();
      fetchAssignments();
    }
  }, [user, fetchBalance, fetchTransactions, fetchAssignments]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
      });
      toast.success('Profile updated');
      setEditing(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyReferral = () => {
    navigator.clipboard.writeText(user?.referralCode || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Referral code copied!');
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'enterprise': return 'text-purple-400 bg-purple-500/10';
      case 'pro': return 'text-blue-400 bg-blue-500/10';
      case 'starter': return 'text-green-400 bg-green-500/10';
      default: return 'text-slate-400 bg-slate-500/10';
    }
  };

if (!user) {
    return null;
  }

  const activeAssignments = assignments.filter(a => a.status === 'active');
  const totalRewards = balance?.buckets.approved || 0;
  const thisMonthRewards = transactions
    .filter(t => t.type === 'reward' && new Date(t.createdAt).getMonth() === new Date().getMonth())
    .reduce((sum, t) => sum + t.amount, 0);

  if (!user) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Profile</h1>
          <p className="text-slate-400 mt-1">Manage your account and view your stats</p>
        </div>
      </div>

      <Card className="glass">
        <CardContent className="pt-6">
          <div className="flex items-center gap-6">
            <Avatar className="h-24 w-24">
              <AvatarImage src={user?.email ? `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}` : undefined} alt={user?.email} />
              <AvatarFallback className="text-2xl">{getInitials(user?.email)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-white">{user.firstName} {user.lastName}</h2>
                  <p className="text-slate-400">{user.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={cn(getTierColor(user.tier))}>
                    {user.tier.charAt(0).toUpperCase() + user.tier.slice(1)}
                  </Badge>
                  <Badge variant="secondary" className="capitalize">{user.role}</Badge>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-1"><Wallet className="h-4 w-4" /> Balance: {formatCurrency(totalRewards)}</span>
                <span className="flex items-center gap-1"><Smartphone className="h-4 w-4" /> Numbers: {activeAssignments.length}</span>
                <span className="flex items-center gap-1"><Users className="h-4 w-4" /> Referrals: {user.totalReferrals || 0}</span>
                <span className="flex items-center gap-1"><Shield className="h-4 w-4" /> 2FA: {user.twoFactorEnabled ? 'Enabled' : 'Disabled'}</span>
              </div>
              {!editing && (
                <Button variant="outline" className="mt-4" onClick={() => setEditing(true)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Profile
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {editing && (
        <Card className="glass">
          <CardHeader>
            <CardTitle>Edit Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">First Name</label>
                <input
                  type="text"
                  value={profileData.firstName}
                  onChange={e => setProfileData({ ...profileData, firstName: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Last Name</label>
                <input
                  type="text"
                  value={profileData.lastName}
                  onChange={e => setProfileData({ ...profileData, lastName: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Save Changes'}
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={(v: any) => setActiveTab(v)} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5 bg-slate-800/50">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="wallet">Wallet</TabsTrigger>
          <TabsTrigger value="numbers">Numbers</TabsTrigger>
          <TabsTrigger value="referrals">Referrals</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid gap-4 md:grid-cols-4">
            <Card className="glass">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Total Balance</p>
                    <p className="text-2xl font-bold text-white mt-1">{formatCurrency(totalRewards)}</p>
                  </div>
                  <div className="p-3 bg-green-500/10 rounded-xl">
                    <Wallet className="h-6 w-6 text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="glass">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">This Month</p>
                    <p className="text-2xl font-bold text-green-400 mt-1">{formatCurrency(thisMonthRewards)}</p>
                  </div>
                  <div className="p-3 bg-cyan-500/10 rounded-xl">
                    <TrendingUp className="h-6 w-6 text-cyan-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="glass">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Active Numbers</p>
                    <p className="text-2xl font-bold text-white mt-1">{activeAssignments.length}</p>
                  </div>
                  <div className="p-3 bg-purple-500/10 rounded-xl">
                    <Smartphone className="h-6 w-6 text-purple-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="glass">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">Referrals</p>
                    <p className="text-2xl font-bold text-white mt-1">{user.totalReferrals || 0}</p>
                  </div>
                  <div className="p-3 bg-amber-500/10 rounded-xl">
                    <Users className="h-6 w-6 text-amber-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-2 mt-6">
            <Card className="glass">
              <CardHeader>
                <CardTitle>Your Referral Code</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500/10 rounded-lg">
                      <Users className="h-5 w-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Referral Code</p>
                      <p className="font-mono text-xl font-bold text-white">{user.referralCode}</p>
                    </div>
                  </div>
                  <Button variant="outline" onClick={handleCopyReferral}>
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
                <p className="text-sm text-slate-400 mt-4">Share your referral code. When someone signs up with your code, you both earn rewards!</p>
              </CardContent>
            </Card>

            <Card className="glass">
              <CardHeader>
                <CardTitle>Account Verification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/10 rounded-lg">
                      <Mail className="h-5 w-5 text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">Email Verified</p>
                      <p className="text-sm text-slate-400">Your email address is confirmed</p>
                    </div>
                  </div>
                  <Badge className={cn(user.emailVerified ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400')}>
                    {user.emailVerified ? 'Verified' : 'Not Verified'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500/10 rounded-lg">
                      <Shield className="h-5 w-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">Two-Factor Authentication</p>
                      <p className="text-sm text-slate-400">Additional security for your account</p>
                    </div>
                  </div>
                  <Badge className={cn(user.twoFactorEnabled ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400')}>
                    {user.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/10 rounded-lg">
                      <Phone className="h-5 w-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">Phone Verified</p>
                      <p className="text-sm text-slate-400">Phone number verification status</p>
                    </div>
                  </div>
                  <Badge className={cn(user.phoneVerified ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400')}>
                    {user.phoneVerified ? 'Verified' : 'Not Verified'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="wallet">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Wallet Balance</CardTitle>
            </CardHeader>
            <CardContent>
              {balance && (
                <div className="grid gap-4 md:grid-cols-4">
                  <div className="p-4 bg-green-500/10 rounded-xl">
                    <p className="text-sm text-green-400">Withdrawable</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(balance.buckets.withdrawable)}</p>
                  </div>
                  <div className="p-4 bg-amber-500/10 rounded-xl">
                    <p className="text-sm text-amber-400">Pending</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(balance.buckets.pending)}</p>
                  </div>
                  <div className="p-4 bg-blue-500/10 rounded-xl">
                    <p className="text-sm text-blue-400">Approved</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(balance.buckets.approved)}</p>
                  </div>
                  <div className="p-4 bg-purple-500/10 rounded-xl">
                    <p className="text-sm text-purple-400">Bonuses</p>
                    <p className="text-2xl font-bold text-white">{formatCurrency(balance.buckets.bonus)}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="numbers">
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Your Numbers</CardTitle>
                <Link to="/numbers" className="text-cyan-400 hover:text-cyan-300 text-sm">View All</Link>
              </div>
            </CardHeader>
            <CardContent>
              {activeAssignments.length > 0 ? (
                <div className="space-y-3">
                  {activeAssignments.map(assignment => (
                    <div key={assignment.id} className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-green-500/10 rounded-lg">
                          <Smartphone className="h-5 w-5 text-green-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white font-mono">{assignment.phoneNumber}</p>
                          <p className="text-sm text-slate-400">${assignment.monthlyReward}/mo • Expires {assignment.expiresAt ? formatDateTime(assignment.expiresAt) : 'Auto-renew'}</p>
                        </div>
                      </div>
                      <Badge className="bg-green-500/10 text-green-400">Active</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Smartphone className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No active numbers</p>
                  <Link to="/numbers" className="inline-flex items-center gap-2 mt-4 text-cyan-400 hover:text-cyan-300">
                    Get a Number <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="referrals">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Referral Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <Card className="glass p-4">
                  <p className="text-sm text-slate-400">Total Referrals</p>
                  <p className="text-3xl font-bold text-white">{user.totalReferrals || 0}</p>
                </Card>
                <Card className="glass p-4">
                  <p className="text-sm text-slate-400">Pending</p>
                  <p className="text-3xl font-bold text-amber-400">0</p>
                </Card>
                <Card className="glass p-4">
                  <p className="text-sm text-slate-400">Approved</p>
                  <p className="text-3xl font-bold text-green-400">{user.totalReferrals || 0}</p>
                </Card>
              </div>
              <div className="mt-6 p-4 bg-slate-800/50 rounded-xl">
                <p className="text-sm text-slate-400">Your referral link:</p>
                <div className="flex gap-2 mt-2">
                  <input
                    type="text"
                    value={`${window.location.origin}/register?ref=${user.referralCode}`}
                    readOnly
                    className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white font-mono text-sm"
                  />
                  <Button variant="outline" onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/register?ref=${user.referralCode}`);
                    toast.success('Link copied!');
                  }}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length > 0 ? (
                <div className="space-y-3">
                  {transactions.slice(0, 10).map(tx => (
                    <div key={tx.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-cyan-500/10 rounded-lg">
                          <CreditCard className="h-4 w-4 text-cyan-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white capitalize">{tx.type}</p>
                          <p className="text-sm text-slate-400">{tx.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={cn('font-bold', tx.amount >= 0 ? 'text-green-400' : 'text-red-400')}>
                          {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                        </p>
                        <p className="text-xs text-slate-500">{formatDateTime(tx.createdAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 text-slate-600" />
                  <p>No transactions yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
